import { ChevronRight } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Card, PageHeader } from "../../components/ui/patrones";
import { Aviso, Boton, Campo, Interruptor } from "../../components/ui/primitivos";
import {
  actualizarCliente,
  type ContactoCliente,
  crearCliente,
  fetchCliente,
} from "../../lib/consola-api";
import { useDatos } from "../../lib/useDatos";
import { SeccionContactos } from "./contactos";

/**
 * Alta y edición de un cliente, porteadas de `ClientForm.tsx` (ADR 0069).
 *
 * **Son pantallas y no un modal** (ADR 0064), con las dos tarjetas de allá:
 * **Datos del cliente** y **Contactos**. En el modal los contactos entraban
 * solo al editar y con `ancho`, cuatro campos por fila dentro de un cuadro.
 *
 * `/clientes/nuevo` y `/clientes/:id/editar` son la misma pantalla, como allá
 * `/clients/new` y `/clients/:id/edit`.
 */
const TIPOS = ["WHITE", "BLACK", "INTERNO"] as const;

export function ClientePagina() {
  const { id } = useParams<{ id: string }>();
  const navegar = useNavigate();
  const esNuevo = id === undefined;

  const detalle = useDatos(() => (id ? fetchCliente(id) : Promise.resolve(null)), id ?? "");

  if (detalle.error) {
    return (
      <div className="space-y-6">
        <PageHeader titulo="Cliente" alVolver={() => navegar("/clientes")} />
        <Aviso>{detalle.error}</Aviso>
      </div>
    );
  }
  if (!esNuevo && !detalle.datos) {
    return <p className="p-8 text-center text-neutral-500">Cargando cliente…</p>;
  }

  return (
    <Formulario
      // Remontar al cambiar de cliente: el formulario arranca su estado de las
      // props, y sin la clave quedaría con los datos del anterior.
      key={id ?? "nuevo"}
      cliente={detalle.datos?.cliente ?? null}
      contactosIniciales={detalle.datos?.contactos ?? []}
      alSalir={() => navegar("/clientes")}
    />
  );
}

type Cliente = NonNullable<Awaited<ReturnType<typeof fetchCliente>>>["cliente"];

function Formulario({
  cliente,
  contactosIniciales,
  alSalir,
}: {
  cliente: Cliente | null;
  contactosIniciales: readonly ContactoCliente[];
  alSalir: () => void;
}) {
  const [datos, setDatos] = useState({
    razonSocial: cliente?.razonSocial ?? "",
    nombreFantasia: cliente?.nombreFantasia ?? "",
    cuit: cliente?.cuit ?? "",
    direccion: cliente?.direccion ?? "",
    observaciones: cliente?.observaciones ?? "",
    tipoCliente: cliente?.tipoCliente ?? "WHITE",
    esVip: cliente?.esVip ?? false,
  });
  const [contactos, setContactos] = useState<ContactoCliente[]>([...contactosIniciales]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Un cliente nuevo arranca con un contacto en blanco, como `ClientForm`:
  // sin al menos uno no hay a quién mandarle el presupuesto, y pedirlo con un
  // formulario vacío se lee como opcional.
  useEffect(() => {
    if (cliente === null && contactos.length === 0) {
      setContactos([contactoEnBlanco(0, true)]);
    }
  }, [cliente, contactos.length]);

  async function guardar() {
    setGuardando(true);
    setError(null);
    try {
      const cuerpo: Record<string, unknown> = {
        ...datos,
        cuit: datos.cuit.trim() || null,
        direccion: datos.direccion.trim() || null,
        observaciones: datos.observaciones.trim() || null,
      };
      // Se mandan solo si hay: una lista vacía la rechaza el worker a propósito
      // —no se borran todos de un saque— y `contactos` ausente significa «no
      // los toques».
      const conDatos = contactos.filter((c) => (c.email ?? "") !== "" || (c.telefono ?? "") !== "");
      if (conDatos.length > 0) {
        cuerpo.contactos = conDatos.map(({ id: _id, ...resto }) => resto);
      }
      if (cliente) await actualizarCliente(cliente.id, cuerpo);
      else await crearCliente(cuerpo);
      alSalir();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo guardar");
    } finally {
      setGuardando(false);
    }
  }

  const listo = !guardando && datos.razonSocial.trim() !== "" && datos.nombreFantasia.trim() !== "";

  return (
    <div className="space-y-6">
      <nav aria-label="Miga de pan" className="flex items-center text-neutral-500 text-sm">
        <Link to="/clientes" className="hover:text-neutral-900">
          Clientes
        </Link>
        <ChevronRight className="mx-2 h-4 w-4" />
        <span className="font-medium text-neutral-900">
          {cliente ? cliente.razonSocial : "Nuevo cliente"}
        </span>
      </nav>

      <PageHeader
        titulo={cliente ? "Editar cliente" : "Nuevo cliente"}
        subtitulo={
          cliente
            ? "Modificá los datos del cliente y sus contactos"
            : "Registrá un cliente nuevo con al menos un contacto"
        }
        alVolver={alSalir}
        acciones={
          <>
            <Boton variante="secundario" disabled={guardando} onClick={alSalir}>
              Cancelar
            </Boton>
            <Boton disabled={!listo} onClick={guardar}>
              {guardando ? "Guardando..." : "Guardar"}
            </Boton>
          </>
        }
      />

      {error ? <Aviso>{error}</Aviso> : null}

      <div className="mx-auto max-w-3xl space-y-6">
        <Seccion titulo="Datos del cliente" descripcion="Información general">
          <Campo
            etiqueta="Razón social"
            value={datos.razonSocial}
            onChange={(e) => setDatos((d) => ({ ...d, razonSocial: e.target.value }))}
            placeholder="Ej: Empresa S.A."
          />
          <Campo
            etiqueta="Nombre de fantasía"
            value={datos.nombreFantasia}
            onChange={(e) => setDatos((d) => ({ ...d, nombreFantasia: e.target.value }))}
          />
          <Campo
            etiqueta="CUIT / DNI"
            value={datos.cuit}
            onChange={(e) => setDatos((d) => ({ ...d, cuit: e.target.value }))}
          />

          <div className="space-y-1">
            <label htmlFor="tipo-cliente" className="block text-neutral-600 text-sm">
              Tipo de cliente
            </label>
            <select
              id="tipo-cliente"
              value={datos.tipoCliente}
              onChange={(e) => setDatos((d) => ({ ...d, tipoCliente: e.target.value }))}
              className="w-full rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-neutral-900 text-sm"
            >
              {TIPOS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2 text-neutral-700 text-sm">
            <Interruptor
              aria-label="Cliente VIP"
              checked={datos.esVip}
              onCheckedChange={(v) => setDatos((d) => ({ ...d, esVip: v }))}
            />
            <span aria-hidden="true">Cliente VIP (habilita el precio VIP de la tarifa)</span>
          </div>

          <Campo
            etiqueta="Dirección"
            value={datos.direccion}
            onChange={(e) => setDatos((d) => ({ ...d, direccion: e.target.value }))}
          />
          <Campo
            etiqueta="Notas internas"
            value={datos.observaciones}
            onChange={(e) => setDatos((d) => ({ ...d, observaciones: e.target.value }))}
          />
        </Seccion>

        <SeccionContactos contactos={contactos} cargando={false} alCambiar={setContactos} />
      </div>
    </div>
  );
}

/** Un contacto vacío, con la clave que necesita React. */
export function contactoEnBlanco(indice: number, principal: boolean): ContactoCliente {
  return {
    id: `nuevo-${indice}`,
    nombre: "",
    email: "",
    telefono: "",
    observacion: "",
    esPredeterminado: principal,
  };
}

function Seccion({
  titulo,
  descripcion,
  children,
}: {
  titulo: string;
  descripcion: string;
  children: ReactNode;
}) {
  return (
    <Card className="space-y-3 p-4">
      <div>
        <h2 className="font-semibold text-neutral-900 text-sm">{titulo}</h2>
        <p className="text-neutral-500 text-sm">{descripcion}</p>
      </div>
      {children}
    </Card>
  );
}
